// File: src/components/admin/analysis-tasks/TemplateGallery.js
// Template gallery for browsing and selecting analysis task templates (Issue #64)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Chip, CircularProgress, Alert,
    Grid, Card, CardContent, CardActions, TextField, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider, InputAdornment,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Search as SearchIcon,
    Rocket as RocketIcon,
    TrendingUp as TrendingUpIcon,
    Loyalty as LoyaltyIcon,
    Loop as LoopIcon,
    Stars as StarsIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';

const CATEGORIES = [
    { id: 'all', label: 'All', icon: <StarsIcon /> },
    { id: 'onboarding', label: 'Onboarding', icon: <RocketIcon /> },
    { id: 'optimization', label: 'Optimization', icon: <TrendingUpIcon /> },
    { id: 'retention', label: 'Retention', icon: <LoyaltyIcon /> },
    { id: 'growth', label: 'Growth', icon: <TrendingUpIcon /> },
    { id: 'lifecycle', label: 'Lifecycle', icon: <LoopIcon /> },
];

const CATEGORY_COLORS = {
    onboarding: '#1976d2',
    optimization: '#2e7d32',
    retention: '#ed6c02',
    growth: '#9c27b0',
    lifecycle: '#0288d1',
};

const TemplateGallery = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const result = await adminApi.getTemplates();
                setTemplates(result.templates || []);
            } catch (err) {
                const errorInfo = handleAdminApiError(err);
                setError(errorInfo.message);
            } finally {
                setLoading(false);
            }
        };
        fetchTemplates();
    }, []);

    const filteredTemplates = templates.filter(tpl => {
        const matchesCategory = categoryFilter === 'all' || tpl.category === categoryFilter;
        const matchesSearch = !searchQuery ||
            tpl.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            tpl.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (tpl.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesCategory && matchesSearch;
    });

    const handlePreview = async (templateId) => {
        setPreviewLoading(true);
        try {
            const template = await adminApi.getTemplate(templateId);
            setPreviewTemplate(template);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo.message);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleUseTemplate = async (template) => {
        // Navigate to create view with template config as state
        let config = template.config || previewTemplate?.config;
        const metadata = template.metadata || previewTemplate?.metadata || template;

        // List items only have metadata — fetch full template for config
        if (!config && metadata.id) {
            try {
                const full = await adminApi.getTemplate(metadata.id);
                config = full.config;
            } catch (err) {
                const errorInfo = handleAdminApiError(err);
                setError(errorInfo.message);
                return;
            }
        }

        navigate('/analysis-tasks/new/from-template', {
            state: {
                templateConfig: config,
                templateName: metadata.name || metadata.id,
                templateId: metadata.id,
            },
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box data-testid="template-gallery">
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/analysis-tasks')}
                    sx={{ mr: 2, color: 'text.secondary' }}
                >
                    Back
                </Button>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Task Templates
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Search and Filter */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search templates..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Tabs
                            value={categoryFilter}
                            onChange={(e, val) => setCategoryFilter(val)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {CATEGORIES.map(cat => (
                                <Tab
                                    key={cat.id}
                                    value={cat.id}
                                    label={cat.label}
                                    icon={cat.icon}
                                    iconPosition="start"
                                    sx={{ minHeight: 48, textTransform: 'none' }}
                                />
                            ))}
                        </Tabs>
                    </Grid>
                </Grid>
            </Paper>

            {/* Template Grid */}
            <Grid container spacing={2}>
                {filteredTemplates.map(tpl => (
                    <Grid item xs={12} sm={6} md={4} key={tpl.id}>
                        <Card
                            data-testid={`template-card-${tpl.id}`}
                            sx={{
                                height: '100%', display: 'flex', flexDirection: 'column',
                                '&:hover': { boxShadow: 4 },
                                cursor: 'pointer',
                            }}
                            onClick={() => handlePreview(tpl.id)}
                        >
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Chip
                                        label={tpl.category}
                                        size="small"
                                        sx={{
                                            backgroundColor: CATEGORY_COLORS[tpl.category] || '#666',
                                            color: '#fff',
                                            mr: 1,
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        v{tpl.version}
                                    </Typography>
                                </Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                    {tpl.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {tpl.description}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(tpl.tags || []).map(tag => (
                                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                                    ))}
                                </Box>
                            </CardContent>
                            <CardActions>
                                <Button
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); handlePreview(tpl.id); }}
                                >
                                    Preview
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={(e) => { e.stopPropagation(); handleUseTemplate(tpl); }}
                                >
                                    Use Template
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
                {filteredTemplates.length === 0 && (
                    <Grid item xs={12}>
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No templates found matching your criteria.
                        </Typography>
                    </Grid>
                )}
            </Grid>

            {/* Preview Dialog */}
            <Dialog
                open={!!previewTemplate}
                onClose={() => setPreviewTemplate(null)}
                maxWidth="md"
                fullWidth
            >
                {previewTemplate && (
                    <>
                        <DialogTitle>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={previewTemplate.metadata.category}
                                    size="small"
                                    sx={{
                                        backgroundColor: CATEGORY_COLORS[previewTemplate.metadata.category] || '#666',
                                        color: '#fff',
                                    }}
                                />
                                {previewTemplate.metadata.name}
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {previewTemplate.metadata.description}
                            </Typography>
                            <Divider sx={{ my: 2 }} />

                            {previewTemplate.documentation && (
                                <>
                                    <Typography variant="subtitle2" gutterBottom>What it does</Typography>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        {previewTemplate.documentation.whatItDoes}
                                    </Typography>

                                    <Typography variant="subtitle2" gutterBottom>Why these tools</Typography>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        {previewTemplate.documentation.whyTheseTools}
                                    </Typography>

                                    <Typography variant="subtitle2" gutterBottom>Why this tier</Typography>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        {previewTemplate.documentation.whyThisTier}
                                    </Typography>

                                    <Typography variant="subtitle2" gutterBottom>Why this trigger</Typography>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        {previewTemplate.documentation.whyThisTrigger}
                                    </Typography>

                                    {previewTemplate.documentation.customizationHints && (
                                        <>
                                            <Typography variant="subtitle2" gutterBottom>Customization hints</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                                {previewTemplate.documentation.customizationHints.map((hint, i) => (
                                                    <li key={i}>
                                                        <Typography variant="body2">{hint}</Typography>
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </>
                            )}

                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" gutterBottom>Configuration preview</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                <Chip label={`Tier: ${previewTemplate.config.dataAccessTier}`} size="small" />
                                <Chip label={`Type: ${previewTemplate.config.notificationType}`} size="small" />
                                <Chip label={`Tools: ${(previewTemplate.config.enabledTools || []).length}`} size="small" />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                Expected frequency: {previewTemplate.metadata.expectedFrequency}
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setPreviewTemplate(null)}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    handleUseTemplate(previewTemplate);
                                    setPreviewTemplate(null);
                                }}
                            >
                                Use Template
                            </Button>
                        </DialogActions>
                    </>
                )}
                {previewLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
            </Dialog>
        </Box>
    );
};

export default TemplateGallery;
